import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  UserCircle, ArrowsClockwise, DownloadSimple, Trash,
  Upload, CheckCircle, WarningCircle, Sparkle, Image,
  SlidersHorizontal, Eye, PaintBrush, ShieldCheck
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { CreditGateModal } from '../components/CreditGateModal';
import { ShareResultButton } from '../components/ShareResultButton';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { pixelCharacterGenerated } from '../utils/pixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STYLES = [
  { value: 'bratz', label: 'Bratz Doll', desc: 'Glamorous doll with dramatic lashes & glossy lips', gradient: 'from-rose-500/15 to-amber-500/15', border: 'border-rose-500/25' },
  { value: 'anime', label: 'Anime', desc: 'Japanese animation style with expressive eyes', gradient: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30' },
  { value: 'pixar', label: 'Pixar 3D', desc: 'Friendly 3D animated character with warm lighting', gradient: 'from-yellow-500/20 to-orange-500/20', border: 'border-yellow-500/30' },
  { value: 'comic', label: 'Comic Hero', desc: 'Bold comic book superhero style', gradient: 'from-red-500/20 to-rose-500/20', border: 'border-red-500/30' },
  { value: 'chibi', label: 'Chibi', desc: 'Adorable kawaii with big head & tiny body', gradient: 'from-green-500/20 to-emerald-500/20', border: 'border-green-500/30' },
  { value: 'pop_art', label: 'Pop Art', desc: 'Andy Warhol inspired bold colors & dots', gradient: 'from-violet-500/20 to-fuchsia-500/20', border: 'border-violet-500/30' },
  { value: 'luxury_fashion', label: 'Luxury Fashion', desc: 'High fashion editorial, Vogue cover energy', gradient: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/30' },
  { value: 'afrofuturist', label: 'Afrofuturist', desc: 'Bold African-inspired futuristic aesthetic', gradient: 'from-orange-500/20 to-amber-500/20', border: 'border-orange-500/30' },
  { value: 'retro_90s', label: 'Retro 90s', desc: 'Nostalgic Saturday morning cartoon feel', gradient: 'from-teal-500/20 to-cyan-500/20', border: 'border-teal-500/30' },
  { value: 'realistic_plus', label: 'Realistic+', desc: 'Enhanced photorealistic professional portrait', gradient: 'from-stone-500/20 to-zinc-500/20', border: 'border-stone-500/30' },
  { value: 'neon_cyberpunk', label: 'Neon Cyberpunk', desc: 'Dark neon lighting, futuristic city aesthetic', gradient: 'from-cyan-500/20 to-blue-500/20', border: 'border-cyan-500/30' },
  { value: 'watercolor', label: 'Watercolor', desc: 'Soft painterly finish, artistic premium feel', gradient: 'from-sky-500/20 to-indigo-500/20', border: 'border-sky-500/30' },
];

const USE_FOR_OPTIONS = [
  { value: 'all', label: 'All purposes' },
  { value: 'avatar', label: 'Profile avatar' },
  { value: 'brand_mascot', label: 'Brand mascot' },
  { value: 'ad_asset', label: 'Ad creative asset' }
];

const FIDELITY_MODES = [
  { value: 'exact_likeness', label: 'Exact Likeness', desc: 'Maximum identity preservation — style adapts to your face', icon: Eye },
  { value: 'style_first', label: 'Style First', desc: 'Stronger style expression — looser resemblance', icon: PaintBrush },
];

const STYLE_INTENSITIES = [
  { value: 'low', label: 'Low', desc: 'Subtle — closest to realistic' },
  { value: 'balanced', label: 'Balanced', desc: 'Clear style, preserved identity' },
  { value: 'high', label: 'High', desc: 'Bold artistic expression' },
];

const PROGRESS_STEPS = {
  queued: { label: 'Initializing', pct: 5 },
  queued_for_generation: { label: 'Creating your character', pct: 15 },
  starting: { label: 'Initializing', pct: 10 },
  extracting_identity: { label: 'Analyzing your face', pct: 25 },
  building_prompt: { label: 'Building style prompt', pct: 40 },
  generating: { label: 'Creating your character', pct: 55 },
  regenerating_for_likeness: { label: 'Improving likeness', pct: 65 },
  retrying_attempt_2: { label: 'Refining (attempt 2)', pct: 70 },
  retrying_attempt_3: { label: 'Final attempt', pct: 80 },
  checking_likeness: { label: 'Verifying identity match', pct: 90 },
  completed: { label: 'Complete', pct: 100 },
};

export const CharacterGeneratorPage = () => {
  const [selectedStyle, setSelectedStyle] = useState('bratz');
  const [useFor, setUseFor] = useState('all');
  const [fidelityMode, setFidelityMode] = useState('exact_likeness');
  const [styleIntensity, setStyleIntensity] = useState('balanced');
  const [customPrompt, setCustomPrompt] = useState('');
  const [sourceFile, setSourceFile] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [currentChar, setCurrentChar] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState(null);
  const [error, setError] = useState('');
  const [showCreditGate, setShowCreditGate] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchCharacters();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchCharacters = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/list`);
      setCharacters(data.characters || []);
    } catch {}
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
      setError('Please upload a PNG, JPG, or WEBP image');
      return;
    }
    setSourceFile(file);
    setSourcePreview(URL.createObjectURL(file));
    setError('');
  };

  const pollStatus = useCallback(async (charId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/status/${charId}`);
      setCurrentChar(data);
      if (data.status === 'completed') {
        clearInterval(pollRef.current);
        setGenerating(false);
        setSelectedChar(data);
        fetchCharacters();
        pixelCharacterGenerated(selectedStyle);
      } else if (data.status === 'failed') {
        clearInterval(pollRef.current);
        setGenerating(false);
        setError(data.error || 'Character generation failed.');
      }
    } catch {
      clearInterval(pollRef.current);
      setGenerating(false);
    }
  }, []);

  const generateCharacter = async () => {
    setError('');
    setGenerating(true);
    setCurrentChar(null);
    setSelectedChar(null);

    try {
      const formData = new FormData();
      formData.append('style', selectedStyle);
      formData.append('use_for', useFor);
      formData.append('fidelity_mode', fidelityMode);
      formData.append('style_intensity', styleIntensity);
      if (customPrompt) formData.append('custom_prompt', customPrompt);
      if (sourceFile) formData.append('source_image', sourceFile);

      const { data } = await axios.post(`${API_URL}/api/characters/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setCurrentChar({
        char_id: data.char_id,
        status: data.status || 'generating',
        progress_step: data.status === 'queued' ? 'queued' : 'starting',
        engine: data.engine,
        queue_position: data.queue_position || 0,
      });
      pollRef.current = setInterval(() => pollStatus(data.char_id), 3000);
    } catch (err) {
      setGenerating(false);
      if (err.response?.status === 402) {
        setShowCreditGate(true);
      } else if (err.response?.status === 403) {
        const d = err.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || 'Character creation requires credits.'));
      } else {
        const d = err.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || 'Failed to generate character.'));
      }
    }
  };

  const downloadChar = (char) => {
    if (char?.image_url) {
      const a = document.createElement('a');
      a.href = `${API_URL}${char.image_url}`;
      a.download = `scalitos-character-${char.style}.png`;
      a.click();
    }
  };

  const freeRegenerate = async () => {
    setError('');
    setGenerating(true);
    setCurrentChar(null);
    setSelectedChar(null);
    try {
      const formData = new FormData();
      formData.append('style', selectedStyle);
      formData.append('use_for', useFor);
      formData.append('fidelity_mode', fidelityMode);
      formData.append('style_intensity', styleIntensity);
      formData.append('is_free_regen', 'true');
      if (customPrompt) formData.append('custom_prompt', customPrompt);
      if (sourceFile) formData.append('source_image', sourceFile);
      const { data } = await axios.post(`${API_URL}/api/characters/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCurrentChar({
        char_id: data.char_id,
        status: data.status || 'generating',
        progress_step: data.status === 'queued' ? 'queued' : 'starting',
        engine: data.engine,
        queue_position: data.queue_position || 0,
      });
      pollRef.current = setInterval(() => pollStatus(data.char_id), 3000);
    } catch (err) {
      setGenerating(false);
      setError('Free regeneration failed. Please try again.');
    }
  };

  const deleteChar = async (charId) => {
    try {
      await axios.delete(`${API_URL}/api/characters/${charId}`);
      setCharacters(prev => prev.filter(c => c.char_id !== charId));
      if (selectedChar?.char_id === charId) setSelectedChar(null);
    } catch {}
  };

  const progressStep = currentChar?.progress_step || 'starting';
  const stepInfo = PROGRESS_STEPS[progressStep] || PROGRESS_STEPS.starting;

  return (
    <DashboardLayout>
      <div data-testid="character-generator-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl border border-white/10 flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)' }}>
              <Sparkle size={20} style={{ color: '#c8f060' }} />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Character Creator</h1>
              <p className="text-zinc-400 text-sm">Turn photos into stylized brand characters — your face, preserved</p>
            </div>
            <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider" style={{ background: '#1a1a1a', color: '#c8f060', border: '1px solid rgba(200,240,96,0.18)' }}>PRO</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-1 space-y-4">
            {/* Upload Photo */}
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
              <h2 className="font-medium font-['Instrument_Serif',serif] text-sm uppercase tracking-wider text-zinc-400">Upload Photo</h2>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="file-input" />
              <button onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn" className="w-full border border-dashed border-white/20 rounded-xl py-8 text-sm text-zinc-400 hover:text-white hover:border-white/40 transition-colors flex flex-col items-center gap-2">
                {sourcePreview ? (
                  <img src={sourcePreview} alt="Source" className="w-24 h-24 rounded-xl object-cover" />
                ) : (
                  <><Upload size={28} className="text-zinc-600" /><span>Upload your photo</span><span className="text-xs text-zinc-600">PNG, JPG, or WEBP</span></>
                )}
              </button>
              {sourceFile && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Image size={14} />
                  <span className="truncate">{sourceFile.name}</span>
                  <button onClick={() => { setSourceFile(null); setSourcePreview(null); }} className="ml-auto text-zinc-600 hover:text-red-400">
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Identity Mode */}
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
              <h2 className="font-medium font-['Instrument_Serif',serif] text-sm uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#c8f060]" />Identity Mode
              </h2>
              <div className="space-y-2">
                {FIDELITY_MODES.map(m => (
                  <button
                    key={m.value}
                    onClick={() => setFidelityMode(m.value)}
                    data-testid={`fidelity-${m.value}`}
                    className={`w-full p-3 rounded-xl text-left transition-all flex items-start gap-3 ${
                      fidelityMode === m.value
                        ? 'bg-[#c8f060]/10 border border-[#c8f060]/30'
                        : 'bg-zinc-800/50 border border-white/5 hover:border-white/20'
                    }`}
                  >
                    <m.icon size={18} weight={fidelityMode === m.value ? 'fill' : 'regular'} className={fidelityMode === m.value ? 'text-[#c8f060] mt-0.5' : 'text-zinc-500 mt-0.5'} />
                    <div>
                      <p className={`text-sm font-medium ${fidelityMode === m.value ? 'text-[#c8f060]' : 'text-white'}`}>{m.label}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{m.desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Style Intensity */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2 flex items-center gap-1.5">
                  <SlidersHorizontal size={12} />Style Strength
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {STYLE_INTENSITIES.map(i => (
                    <button
                      key={i.value}
                      onClick={() => setStyleIntensity(i.value)}
                      data-testid={`intensity-${i.value}`}
                      className={`px-2 py-2 rounded-lg text-center transition-all ${
                        styleIntensity === i.value
                          ? 'bg-white/10 text-white border border-white/20'
                          : 'bg-zinc-800/50 text-zinc-500 border border-transparent hover:border-white/10'
                      }`}
                    >
                      <p className="text-xs font-medium">{i.label}</p>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-600 mt-1.5 text-center">{STYLE_INTENSITIES.find(i => i.value === styleIntensity)?.desc}</p>
              </div>
            </div>

            {/* Choose Style */}
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
              <h2 className="font-medium font-['Instrument_Serif',serif] text-sm uppercase tracking-wider text-zinc-400">Choose Style</h2>
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setSelectedStyle(s.value)}
                    data-testid={`style-${s.value}`}
                    className={`p-3 rounded-xl text-left transition-all ${selectedStyle === s.value ? `bg-gradient-to-br ${s.gradient} ${s.border} border` : 'bg-zinc-800/50 border border-white/5 hover:border-white/20'}`}
                  >
                    <p className="text-sm font-medium text-white">{s.label}</p>
                    <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{s.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
              <h2 className="font-medium font-['Instrument_Serif',serif] text-sm uppercase tracking-wider text-zinc-400">Options</h2>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Anything specific? (optional)</label>
                <textarea value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="e.g., Add sunglasses, use blue hair color..." rows={2} className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30 resize-none" data-testid="custom-prompt-input" />
              </div>

              {error && (
                <div data-testid="char-error" className="text-sm rounded-xl px-4 py-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400">
                  <WarningCircle size={16} />{error}
                </div>
              )}

              <button onClick={generateCharacter} disabled={generating} data-testid="generate-character-btn" className="w-full rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all" style={{ background: '#c8f060', color: '#080808' }}>
                {generating ? <><ArrowsClockwise size={18} className="animate-spin" />Creating character...</> : <><Sparkle size={18} weight="fill" />Create my character · 2 credits</>}
              </button>
            </div>
          </motion.div>

          {/* Right Panel */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2 space-y-6">
            {/* Generation Progress */}
            {generating && currentChar && (
              <div data-testid="generation-progress" className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="text-center py-10">
                  <ArrowsClockwise size={32} className="animate-spin mx-auto mb-4" style={{ color: '#c8f060' }} />
                  <h3 className="font-medium text-lg mb-1">Creating your character</h3>
                  <p className="text-sm text-zinc-400 mb-6">
                    {`AI is transforming into ${STYLES.find(s => s.value === selectedStyle)?.label || selectedStyle} style`}
                  </p>

                  {/* Pipeline steps */}
                  <div className="max-w-sm mx-auto space-y-3">
                    {Object.entries(PROGRESS_STEPS).filter(([k]) => k !== 'completed').map(([key, step]) => {
                      const stepKeys = Object.keys(PROGRESS_STEPS).filter(k => k !== 'completed');
                      const currentIdx = stepKeys.indexOf(progressStep);
                      const thisIdx = stepKeys.indexOf(key);
                      const isDone = thisIdx < currentIdx;
                      const isCurrent = key === progressStep;

                      return (
                        <div key={key} className={`flex items-center gap-3 text-left transition-all ${isCurrent ? 'opacity-100' : isDone ? 'opacity-50' : 'opacity-20'}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isDone ? 'bg-[#c8f060]/20' : isCurrent ? 'bg-[#c8f060]/10' : 'bg-zinc-800'}`}>
                            {isDone ? (
                              <CheckCircle size={14} weight="fill" className="text-[#c8f060]" />
                            ) : isCurrent ? (
                              <ArrowsClockwise size={12} className="animate-spin" style={{ color: '#c8f060' }} />
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
                            )}
                          </div>
                          <span className={`text-xs ${isCurrent ? 'text-white font-medium' : 'text-zinc-500'}`}>{step.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Progress bar */}
                  <div className="max-w-sm mx-auto mt-6">
                    <div className="w-full bg-zinc-800 rounded-full h-1.5">
                      <motion.div
                        className="h-1.5 rounded-full"
                        style={{ background: '#c8f060' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${stepInfo.pct}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <p className="text-zinc-600 text-xs mt-2">This usually takes 30-90 seconds</p>
                  </div>
                </div>
              </div>
            )}

            {/* Character Preview */}
            {selectedChar?.status === 'completed' && selectedChar.image_url && (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium font-['Instrument_Serif',serif]">Your Character</h3>
                  <div className="flex items-center gap-2">
                    <ShareResultButton
                      title={`${STYLES.find(s => s.value === selectedChar.style)?.label || selectedChar.style} Character`}
                      content={`written for you ${selectedChar.style} character${selectedChar.likeness_score ? ` with ${selectedChar.likeness_score}% identity match` : ''}`}
                      type="Character"
                      imageUrl={`${API_URL}${selectedChar.image_url}`}
                    />
                    <button onClick={() => downloadChar(selectedChar)} data-testid="download-char-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
                      <DownloadSimple size={16} />Download
                    </button>
                  </div>
                </div>

                {/* Before / After Slider (when source photo exists) */}
                {sourcePreview ? (
                  <BeforeAfterSlider
                    beforeSrc={sourcePreview}
                    afterSrc={`${API_URL}${selectedChar.image_url}`}
                    beforeLabel="Your Photo"
                    afterLabel={STYLES.find(s => s.value === selectedChar.style)?.label || 'Character'}
                  />
                ) : (
                  <div className="flex items-center justify-center bg-zinc-800/30 rounded-xl p-4">
                    <img src={`${API_URL}${selectedChar.image_url}`} alt="Character" className="max-h-[500px] rounded-xl shadow-2xl" data-testid="character-preview" />
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-zinc-300">{STYLES.find(s => s.value === selectedChar.style)?.label || selectedChar.style}</span>
                  <span className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-zinc-300">{USE_FOR_OPTIONS.find(o => o.value === selectedChar.use_for)?.label || 'All purposes'}</span>
                  {selectedChar.fidelity_mode && (
                    <span className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-zinc-300">{FIDELITY_MODES.find(m => m.value === selectedChar.fidelity_mode)?.label || selectedChar.fidelity_mode}</span>
                  )}
                </div>

                {/* Likeness score feedback */}
                {selectedChar.likeness_score != null && (
                  <div className="mt-4 flex items-center gap-3 p-3 rounded-xl" style={{
                    background: selectedChar.likeness_score >= 70 ? 'rgba(200,240,96,0.05)' : 'rgba(234,179,8,0.05)',
                    border: `1px solid ${selectedChar.likeness_score >= 70 ? 'rgba(200,240,96,0.15)' : 'rgba(234,179,8,0.15)'}`
                  }}>
                    <ShieldCheck size={18} className={selectedChar.likeness_score >= 70 ? 'text-[#c8f060]' : 'text-yellow-500'} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-white">
                        Identity Match: {selectedChar.likeness_score}%
                      </p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">
                        {selectedChar.likeness_details?.face_shape_match && 'Face '}
                        {selectedChar.likeness_details?.skin_tone_match && '· Skin tone '}
                        {selectedChar.likeness_details?.hair_match && '· Hair '}
                        {selectedChar.likeness_details?.overall_recognizable ? '— Recognizable' : '— Loose match'}
                      </p>
                    </div>
                    <button
                      onClick={() => freeRegenerate()}
                      data-testid="free-regen-btn"
                      className="text-xs font-medium px-3 py-1.5 rounded-full transition-colors shrink-0"
                      style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}
                    >
                      Regenerate free
                    </button>
                  </div>
                )}

                {/* Fallback regen if no likeness score */}
                {selectedChar.likeness_score == null && (
                  <div className="mt-4 flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(234,179,8,0.05)', border: '1px solid rgba(234,179,8,0.15)' }}>
                    <div className="flex-1">
                      <p className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>
                        Does this accurately reflect your photo?
                      </p>
                    </div>
                    <button
                      onClick={() => freeRegenerate()}
                      data-testid="free-regen-btn-fallback"
                      className="text-xs font-medium px-3 py-1.5 rounded-full transition-colors shrink-0"
                      style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}
                    >
                      Regenerate free
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {!generating && !selectedChar && (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="text-center py-16">
                  <UserCircle size={48} className="mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-medium mb-2">Create your brand character</h3>
                  <p className="text-sm mb-1">Upload a photo and pick a style to generate a unique character</p>
                  <p className="text-zinc-600 text-xs">Your face is used as the primary reference — the AI preserves your identity</p>
                </div>
              </div>
            )}

            {/* Gallery */}
            {characters.length > 0 && (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <h3 className="font-medium font-['Instrument_Serif',serif] mb-4">Your Characters</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {characters.map(c => (
                    <div key={c.char_id} className={`group relative rounded-xl overflow-hidden border cursor-pointer transition-all ${selectedChar?.char_id === c.char_id ? 'border-[#c8f060]/50 ring-1 ring-[#c8f060]/30' : 'border-white/5 hover:border-white/20'}`}>
                      {c.status === 'completed' && c.image_url ? (
                        <img src={`${API_URL}${c.image_url}`} alt={c.style} className="w-full aspect-square object-cover" onClick={() => setSelectedChar(c)} />
                      ) : c.status === 'generating' ? (
                        <div className="w-full aspect-square bg-zinc-800 flex items-center justify-center">
                          <ArrowsClockwise size={24} className="text-zinc-600 animate-spin" />
                        </div>
                      ) : (
                        <div className="w-full aspect-square bg-zinc-800 flex items-center justify-center">
                          <WarningCircle size={24} className="text-red-500/50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                        <span className="text-xs text-white">{STYLES.find(s => s.value === c.style)?.label || c.style}</span>
                        <div className="ml-auto flex gap-1">
                          {c.status === 'completed' && <button onClick={(e) => { e.stopPropagation(); downloadChar(c); }} className="p-1 bg-white/20 rounded"><DownloadSimple size={12} className="text-white" /></button>}
                          <button onClick={(e) => { e.stopPropagation(); deleteChar(c.char_id); }} className="p-1 bg-white/20 rounded hover:bg-red-500/50"><Trash size={12} className="text-white" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <CreditGateModal
        open={showCreditGate}
        onClose={() => setShowCreditGate(false)}
        requiredCredits={1}
        onBuyCredits={() => { setShowCreditGate(false); window.location.href = '/dashboard/billing'; }}
      />
    </DashboardLayout>
  );
};

export default CharacterGeneratorPage;
